import { Component, Element, h, Host, Prop, State, ComponentInterface, Watch } from '@stencil/core';
import { DefaultLanguages } from '../../../models/DefaultLanguages';
import getEnv from '../../../utils/env';
import { BsWebcomponent } from '../bsWebcomponent';
import { BaTabbar } from '@design/bapf-pattern-library/source/js/_tabbar';
import { Language } from '../../../models/Language';
import { v4 as uuidv4 } from 'uuid';
import { ActiveTab } from './ActiveTab';
import { ActingType } from '@oiambk/oiam-oauth-types';

/**
 * @internal
 */
@Component({
  tag: 'bahf-header-megaflyout',
  styles: '.pb-2 { padding-bottom:1.0rem !important }',
  assetsDirs: ['../assets'],
  shadow: false,
  scoped: false,
})
export class BahfHeaderMegaflyout implements ComponentInterface {
  @Prop() loggedIn: boolean;
  @Prop() hfConfigLanguages: Language[] = [];
  @Prop() languages: {
      activeLanguage: Language;
      detectedLanguage: Language;
  } = { activeLanguage: DefaultLanguages.de, detectedLanguage: DefaultLanguages.de };
  @Prop() jsonData;
  @Prop() actingType: ActingType | undefined;

  @State() isMegaFlyoutVisible: boolean;

  @State() activeTab: ActiveTab;
  @Element() megaflyoutInstance: HTMLElement;

  private readonly ID_MEGAFLYOUT = 'headerFlyoutMain';

  // Tabbar
  tabbarElem: HTMLElement;

  @Watch('languages')
  @Watch('jsonData')
  @Watch('loggedIn')
  @Watch('actingType')
  handleDataChange() {
    this.setActiveTabByUserOrDocument();
  }

  componentWillLoad() {
    this.setActiveTabByUserOrDocument();
  }

  componentDidRender() {
    this.megaflyoutInstance && new BsWebcomponent(this.megaflyoutInstance);
    setTimeout(() => {
      this.tabbarElem &&
        !this.tabbarElem.hasAttribute('data-initialized') &&
        this.megaflyoutInstance.classList.contains('hydrated') &&
        this.megaflyoutInstance.parentNode !== null &&
        new BaTabbar(this.tabbarElem);
    });
  }

  setActiveTabByUserOrDocument() {
    if (this.loggedIn) {
      this.activeTab = this.mapActingTypeToActiveTab(this.actingType);
    } else {
      this.activeTab = this.isLangDe() ? this.getActiveTabFromDocument() : ActiveTab.PRIVATPERSONEN;
    }
  }

  getMegaflyoutCollapsibleId(sub: string, index: number) {
    return `headerFlyout${index}${'Sub' + sub}`;
  }

  private isLangDe(): boolean {
    return this.languages.activeLanguage.code === DefaultLanguages.de.code;
  }

  render() {
    return (
      <Host>
        <button
          data-testid="bahf-button-megaflyout"
          id="button-mega-flyout"
          class={`ba-menu-entry ba-main collapsed`}
          title={this.jsonData?.headline}
          type="button"
          data-bs-toggle={`collapse`}
          data-bs-target={`#${this.ID_MEGAFLYOUT}`}
          aria-expanded={'false'}
          aria-controls={this.ID_MEGAFLYOUT}
        >
          <span class="ba-bars"></span>
          <span class="ba-title">{this.jsonData?.menuButton}</span>
        </button>
        <div class="ba-flyout-container ba-flyout-main">
          <div
            id={this.ID_MEGAFLYOUT}
            class={`ba-flyout-collapse collapse`}
            data-bs-parent=".ba-header"
          >
            <div class="ba-flyout" aria-labelledby="headingMainmenu">
              <div class="row justify-content-between">
                <div class="col-auto">
                  <h2 class={`h4 ${this.isLangDe() ? '' : 'pb-2'}`} id="headingMainmenu">
                    {this.jsonData?.headline}
                  </h2>
                </div>
                {this.isLangDe() && this.renderTabbarTrigger()}
              </div>
              {this.jsonData && this.renderTabbarPanels()}
            </div>
          </div>
        </div>
      </Host>
    );
  }

  private renderTabbarPanels() {
    return (
      <div class="tab-content">
        {!!this.jsonData.privatpersonen && this.renderTabbarPanel('Privatpersonen', this.jsonData.privatpersonen)}
        {!!this.jsonData.unternehmen && this.renderTabbarPanel('Unternehmen', this.jsonData.unternehmen)}
        {!!this.jsonData.institutionen && this.renderTabbarPanel('Institutionen', this.jsonData.institutionen)}

        {this.renderMobileMetaNav()}
      </div>
    );
  }

  private renderTabbarPanel(title: string, data) {
    return (
      <div
        role="tabpanel"
        aria-labelledby={`tab${title}`}
        id={`tabPane${title}`}
        class={`tab-pane fade ${this.activeTab === title.toLowerCase() ? 'show active' : ''}`}
      >
        <div class="row ba-menu-container">
          {/* Breite einer Spalte ist abhängig der Gesamtzahl von Spalten,
            Bsp: 12-12/(3+1) = 9 */}
          <div class={`col-md-6 col-lg-${12-12/(data.lebenslagenRow1.length + 1)} ba-col-main`}>
            {/*lebenslagenRow1*/}
            {data?.lebenslagenRow1 && this.getLebenslagenRow(data.lebenslagenRow1, title, 0)}
            {/*lebenslagenRow2*/}
            {data?.lebenslagenRow2 &&
              this.getLebenslagenRow(data.lebenslagenRow2, title, data.lebenslagenRow1.length)}
          </div>

          {/* Breite einer Spalte ist abhängig der Gesamtzahl von Spalten,
            Bsp: 12/(3+1) = 3. Die Summe mit oben muss 12 ergeben (für 12 Columns). */}
          <div class={`col-md-6 col-lg-${12/(data.lebenslagenRow1.length + 1)} ba-col-eservices`}>
            {data?.eServices && this.getEserviceRow(data.eServices, title + 'EService')}
          </div>
        </div>
      </div>
    );
  }

  private renderMobileMetaNav() {
    return (
      <div class="ba-nav-meta-mobile ba-menu-container">
        <bahf-language-switch-mobile
          hfConfigLanguages={this.hfConfigLanguages}
          currentLanguage={this.languages.detectedLanguage}
          languageLabel={this.jsonData?.languageLabel}
          srSelectionLabel={this.jsonData?.srSelectionLabel}
        />
        <ul
          class="ba-meta-links"
          style={this.languages.detectedLanguage.code !== DefaultLanguages.de.code ? { display: 'none' } : {}}
          aria-hidden={this.isLangDe() ? 'false' : 'true'}
        >
            <li>
              <a href={getEnv().gebaerdenSprachEndpoint}
                 class="ba-link-sign-language"
                 data-testid="bahf-button-sign-language-mobile">
                Gebärdensprache
              </a>
            </li>
            <li>
              <a href={getEnv().leichteSprachEndpoint}
                 class="ba-link-simple-language"
                 data-testid="bahf-button-simple-language-mobile">
                Leichte Sprache
              </a>
            </li>
        </ul>
      </div>
    );
  }

  private renderTabbarTrigger() {
    return (
      <div class="col ba-tabbar-container">
        <nav class="ba-tabbar ba-compact" ref={(el) => (this.tabbarElem = el)}>
          <div class="ba-viewport">
            <ul class="nav" role="tablist" aria-label={this.jsonData?.tabbarNavAriaLabel}>
              <li role="presentation">
                <a
                  role="tab"
                  aria-selected={this.activeTab === ActiveTab.PRIVATPERSONEN}
                  class={this.activeTab === ActiveTab.PRIVATPERSONEN ? 'active' : undefined}
                  id="tabPrivatpersonen"
                  data-testid="bahf-megaflyout-tabPrivatpersonen"
                  href="#tabPanePrivatpersonen"
                  aria-controls="tabPanePrivatpersonen"
                  onClick={(event) => {
                    event.preventDefault();
                    this.activeTab = ActiveTab.PRIVATPERSONEN;
                  }}
                  onFocus={(event) => {
                    event.preventDefault();
                    this.activeTab = ActiveTab.PRIVATPERSONEN;
                  }}
                >
                  Privatpersonen
                </a>
              </li>
              <li role="presentation">
                <a
                  role="tab"
                  aria-selected={this.activeTab === ActiveTab.UNTERNEHMEN}
                  class={this.activeTab === ActiveTab.UNTERNEHMEN ? 'active' : undefined}
                  id="tabUnternehmen"
                  data-testid="bahf-megaflyout-tabUnternehmen"
                  href="#tabPaneUnternehmen"
                  aria-controls="tabPaneUnternehmen"
                  onClick={(event) => {
                    event.preventDefault();
                    this.activeTab = ActiveTab.UNTERNEHMEN;
                  }}
                  onFocus={(event) => {
                    event.preventDefault();
                    this.activeTab = ActiveTab.UNTERNEHMEN;
                  }}
                >
                  Unternehmen
                </a>
              </li>
              <li role="presentation">
                <a
                  role="tab"
                  aria-selected={this.activeTab === ActiveTab.INSTITUTIONEN}
                  class={this.activeTab === ActiveTab.INSTITUTIONEN ? 'active' : undefined}
                  id="tabInstitutionen"
                  data-testid="bahf-megaflyout-tabInstitutionen"
                  href="#tabPaneInstitutionen"
                  aria-controls="tabPaneInstitutionen"
                  onClick={(event) => {
                    event.preventDefault();
                    this.activeTab = ActiveTab.INSTITUTIONEN;
                  }}
                  onFocus={(event) => {
                    event.preventDefault();
                    this.activeTab = ActiveTab.INSTITUTIONEN;
                  }}
                >
                  Institutionen
                </a>
              </li>
              <li class="ba-marker" tabindex="-1" aria-hidden="true" />
            </ul>
          </div>
          <button
            class="ba-prev"
            type="button"
            tabindex="-1"
            title={this.jsonData?.backButton}
            aria-label={this.jsonData?.backButton}
          />
          <button
            class="ba-next"
            type="button"
            tabindex="-1"
            title={this.jsonData?.forwardButton}
            aria-label={this.jsonData?.forwardButton}
          />
        </nav>
      </div>
    );
  }

  private getEserviceRow(data, row: string) {
    return (
      <div class="row">
        <div class="col">
          <button
            class="ba-collapse-button d-md-none collapsed"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target={`#${this.getMegaflyoutCollapsibleId(row, 0)}`}
            aria-expanded={`false`}
            aria-controls={this.getMegaflyoutCollapsibleId(row, 0)}
          >
            {data.title}
          </button>

          <div id={this.getMegaflyoutCollapsibleId(row, 0)} class="collapse d-md-block">
            <div class="ba-menu-title">
              <span class="d-none d-md-inline">{data?.title}</span>
            </div>
            <ul aria-label={data?.topicsAriaLabel}>
              {data.links.map((link) =>
                link.url ? (
                  <li key={link.title}>
                    <a href={link.url}>{link.title}</a>
                  </li>
                ) : (
                  <li key={link.title}>{link.title}</li>
                )
              )}
            </ul>
            {this.loggedIn ? (
              <a
                href={data.alleServicesLink.urlLoggedIn}
                class="ba-link-eservices ba-btn ba-btn-tertiary ba-btn-icon ba-icon-arrow-right-before ba-btn-block"
              >
                <span>{data.alleServicesLink.title}</span>
              </a>
            ) : (
              <a
                href={data.alleServicesLink.url}
                class="ba-link-eservices ba-btn ba-btn-tertiary ba-btn-icon ba-icon-arrow-right-before ba-btn-block"
              >
                <span>{data.alleServicesLink.title}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  private getLebenslagenRow(data, sub: string, offset: number) {
    return (
      <div class="row">
        {data.map((lebenslage, index: number) => {
          return (
            // Breite einer Spalte ist abhängig der Gesamtzahl von Spalten
            <div class={`col-lg-${12/data.length}`} key={uuidv4()}>
              <button
                class="ba-collapse-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#${this.getMegaflyoutCollapsibleId(sub, index + offset)}`}
                aria-expanded={`false`}
                aria-controls={this.getMegaflyoutCollapsibleId(sub, index + offset)}
              >
                {lebenslage.title}
              </button>

              <div
                id={this.getMegaflyoutCollapsibleId(sub, index + offset)}
                class="collapse d-lg-block"
              >
                {lebenslage.url ? (
                  <a
                    href={lebenslage.url}
                    class="ba-menu-title"
                    title={`Zur Themenübersicht ${lebenslage.title}`}
                  >
                    <span class="d-none d-lg-inline">{lebenslage.title}</span>
                    <span class="d-lg-none">Zur Übersicht</span>
                  </a>
                ) : (
                  <div class="ba-menu-title">
                    <span class="d-none d-md-inline">{lebenslage.title}</span>
                  </div>
                )}
                <ul aria-label={data?.topicsAriaLabel}>
                  {lebenslage.links.map((link) =>
                    link.url ? (
                      <li key={link.title}>
                        <a href={link.url}>{link.title}</a>
                      </li>
                    ) : (
                      <li class={this.isLangDe() ? '' : 'pb-2'} key={link.title}>
                        {link.title}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  private getActiveTabFromDocument(): ActiveTab {
    //get audience from document
    const metaAudienceElement: HTMLMetaElement =
      document.head.querySelector("meta[name='audience']");
    const audience: string = metaAudienceElement?.content;

    //map audience to activeTab
    switch (audience?.toLowerCase()) {
      case ActiveTab.INSTITUTIONEN:
        return ActiveTab.INSTITUTIONEN;
      case ActiveTab.UNTERNEHMEN:
        return ActiveTab.UNTERNEHMEN;
      case ActiveTab.PRIVATPERSONEN:
      default:
        return ActiveTab.PRIVATPERSONEN;
    }
  }

  private mapActingTypeToActiveTab(actingType: ActingType): ActiveTab {
    switch (actingType) {
      case 'partner':
        return ActiveTab.INSTITUTIONEN;
      case 'unternehmen':
        return ActiveTab.UNTERNEHMEN;
      case 'privatperson':
      default:
        return ActiveTab.PRIVATPERSONEN;
    }
  }
}
